import { ContextKindKey } from '@gonfalon/contexts/types';
import { fromJS, List, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';
import { toJS } from '@gonfalon/legacy-immutable/immutableUtils';

import { flagKinds } from '#utils/flags/flagConstants';
import { handleRolloutLogic } from '#utils/flags/handleRolloutLogic';
import type { Flag } from '#utils/flags/records/Flag';
import type { Variation } from '#utils/flags/records/Variation';
import type { WeightedVariation } from '#utils/flags/weightedVariationUtils';
import { createWeightedVariation } from '#utils/flags/weightedVariationUtils';

export type ExperimentRollout = {
  canReshuffle: boolean;
  defaultVariation: number;
  type?: string;
};

type RolloutProps = {
  variations: List<WeightedVariation>;
  bucketBy?: string;
  experimentAllocation?: ExperimentRollout;
  contextKind: string;
};
export class Rollout extends Record<RolloutProps>({
  bucketBy: undefined,
  experimentAllocation: undefined,
  variations: List(),
  contextKind: '',
}) {
  getWeightForIndex(variationIndex: number) {
    const match = this.variations.find((wv) => wv.variation === variationIndex && !wv._untracked);
    return match ? match.weight : null;
  }

  getTotalRolloutPercentage() {
    return this.variations.reduce((sum, wv) => (!wv._untracked ? sum + wv.weight : sum + 0), 0);
  }

  setWeightForVariation(flag: Flag, variation: Variation, weight: number) {
    const variations = flag.getVariations();
    const variationIndex = variations.findIndex((v) => v._key === variation._key);
    if (variationIndex === -1) {
      return this;
    }

    if (flag.getKind() === flagKinds.BOOLEAN || variations.size === 2) {
      return flag.createWeightedRollout(variation, weight, this.bucketBy, this.contextKind);
    }

    return handleRolloutLogic(this, variationIndex, weight, variations);
  }

  getContextKind() {
    return this.contextKind || ContextKindKey.USER;
  }

  hasNonUserContext() {
    return this.getContextKind() !== ContextKindKey.USER;
  }

  toRep(): Rollout {
    return fromJS(this.toJSON()).withMutations((map: Rollout) => {
      map.get('bucketBy') === undefined && map.remove('bucketBy');
    });
  }
}

export function createRollout(props: CreateFunctionInput<Rollout> = {}) {
  const rollout = props instanceof Rollout ? props : new Rollout(fromJS(props));

  if (rollout.experimentAllocation) {
    const variations: { [key: string]: { weight: number; variation: number; _untracked?: boolean } } = {};
    rollout.variations.forEach((v) => {
      const wv = createWeightedVariation(v);
      const key = `${wv.get('variation')}:${wv.get('_untracked')}`;
      if (variations[key]) {
        variations[key].weight += wv.get('weight');
      } else {
        variations[key] = {
          weight: wv.get('weight'),
          variation: wv.get('variation'),
          _untracked: wv.get('_untracked'),
        };
      }
    });
    const weightedVariations = Object.values(variations).map((v) => createWeightedVariation(v));
    return rollout
      .set('variations', List.of(...weightedVariations))
      .update('experimentAllocation', (alloc) => (alloc ? createExperimentRollout(alloc) : alloc));
  }
  return rollout.update('variations', (vs) => vs.map(createWeightedVariation));
}

export function createExperimentRollout(
  props: { canReshuffle?: boolean; defaultVariation?: number } = {},
): ExperimentRollout {
  return {
    canReshuffle: true,
    defaultVariation: 0,
    ...toJS(props),
  };
}
